import { useNavigate, Navigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { askPayMethod } from "@/components/billing/PayMethod";

const KEY = "sq_after_auth";

export const rememberIntent = (target) => sessionStorage.setItem(KEY, target);

export function takeIntent() {
  const t = sessionStorage.getItem(KEY);
  sessionStorage.removeItem(KEY);
  return t && /^\/(app|admin\/master)(\/|\?|$)/.test(t) ? t : null;
}

// Maps an in-app checkout target to a pay-first Stripe product (null = keep the register flow).
export function guestSpec(target) {
  const s = baseSpec(target);
  const src = new URLSearchParams(target.split("?")[1] || "").get("src");
  return s && src ? { ...s, source: src, device: window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop" } : s;
}

function baseSpec(target) {
  const [path, qs] = target.split("?");
  const q = new URLSearchParams(qs || "");
  if (path === "/app/abbonamento" && q.get("piano")) return { product: "snapquote", plan: q.get("piano"), interval: q.get("periodo") === "anno" ? "year" : "month" };
  if (path === "/app/founder") return { product: "founders100", interval: q.get("piano") === "mensile" ? "month" : "year", ab: q.get("ab") || "" };
  if (path === "/app/sofia") {
    const plan = q.get("piano") || "essential";
    return ["essential", "essential_annual"].includes(plan) ? { product: "sofia", plan } : null;
  }
  if (path === "/app/sito" && q.get("base")) return { product: "website", base: q.get("base") };
  return null;
}

export async function startGuestCheckout(spec, ask = true) {
  if (ask && (await askPayMethod(spec)) !== "stripe") return false;
  const { data } = await api.post("/public/checkout", { ...spec, origin_url: window.location.origin, return_path: window.location.pathname + window.location.search });
  window.location.href = data.checkout_url;
  return true;
}

// Short shareable link (e.g. /attiva): logged-in users go to the in-app checkout, visitors straight to Stripe.
export function IntentRedirect({ target }) {
  const { user } = useAuth();
  const started = useRef(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (user !== false || started.current) { return; }
    started.current = true;
    const spec = guestSpec(target);
    if (!spec) { rememberIntent(target); setFailed(true); return; }
    startGuestCheckout(spec, false).catch((e) => { toast.error(formatApiError(e)); rememberIntent(target); setFailed(true); });
  }, [user, target]);
  if (user) return <Navigate to={target} replace />;
  if (failed) return <Navigate to="/register" replace />;
  return <div data-testid="intent-redirect-loading" className="min-h-screen bg-[#0B0D10] text-[#A7B0BA] flex items-center justify-center text-sm">Apertura del pagamento sicuro…</div>;
}

// Public buy CTA: logged-in users use the in-app checkout; visitors open Stripe immediately (no login).
export function CheckoutButton({ target, className, style, children, testid, onClick }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (onClick) onClick();
    if (user) return navigate(target);
    const spec = guestSpec(target);
    if (!spec) { rememberIntent(target); return navigate("/register"); }
    setBusy(true);
    try { if (!(await startGuestCheckout(spec))) setBusy(false); } catch (e) {
      if (e?.response?.status === 402) { rememberIntent(target); return navigate("/register"); }
      toast.error(formatApiError(e)); setBusy(false);
    }
  };
  return <button type="button" data-testid={testid} onClick={go} disabled={busy} aria-busy={busy} className={className} style={{ ...style, ...(busy ? { opacity: 0.7, cursor: "wait" } : {}) }}>{busy ? "Apertura pagamento…" : children}</button>;
}
